import React, { useState } from 'react';
import { Motor, Battery, Propeller, SystemConfig, AnalysisResult, TabType } from './types';
import { defaultMotors, defaultBatteries, defaultPropellers } from './data/defaultData';
import SystemConfigTab from './components/SystemConfigTab';
import MotorDatabaseTab from './components/MotorDatabaseTab';
import BatteryDatabaseTab from './components/BatteryDatabaseTab';
import PropellerDatabaseTab from './components/PropellerDatabaseTab';
import AnalysisResultsTab from './components/AnalysisResultsTab';
import OptimizationTab from './components/OptimizationTab';
import DataManagementTab from './components/DataManagementTab';
import StatusNotification from './components/StatusNotification';
import Header from './components/Header';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

function AppContent() {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('system-config');
  const [motors, setMotors] = useState<Motor[]>(defaultMotors);
  const [batteries, setBatteries] = useState<Battery[]>(defaultBatteries);
  const [propellers, setPropellers] = useState<Propeller[]>(defaultPropellers);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig>({
    targetWeight: 1.2,
    otherPower: 15,
    thrustRatio: 2.0,
    bladeCount: 2,
  });
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<'success' | 'warning' | 'error'>('success');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const showStatus = (message: string, type: 'success' | 'warning' | 'error' = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    setTimeout(() => setStatusMessage(''), 3000);
  };

  return (
    <div 
      className="min-h-screen transition-colors duration-200"
      style={{ backgroundColor: colors.background }}
    >
      {/* Header */}
      <Header
        activeTab={activeTab}
        onTabChange={setActiveTab}
        systemConfig={systemConfig}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        {activeTab === 'system-config' && (
          <SystemConfigTab
            config={systemConfig}
            onConfigChange={setSystemConfig}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'motor-database' && (
          <MotorDatabaseTab
            motors={motors}
            onMotorsChange={setMotors}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'battery-database' && (
          <BatteryDatabaseTab
            batteries={batteries}
            onBatteriesChange={setBatteries}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'propeller-database' && (
          <PropellerDatabaseTab
            propellers={propellers}
            onPropellersChange={setPropellers}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'analysis-results' && (
          <AnalysisResultsTab
            motors={motors}
            batteries={batteries}
            propellers={propellers}
            config={systemConfig}
            analysisResults={analysisResults}
            onAnalysisResultsChange={setAnalysisResults}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'optimization' && (
          <OptimizationTab
            analysisResults={analysisResults}
            showStatus={showStatus}
          />
        )}
        {activeTab === 'data-management' && (
          <DataManagementTab
            motors={motors}
            batteries={batteries}
            propellers={propellers}
            config={systemConfig}
            analysisResults={analysisResults}
            onMotorsChange={setMotors}
            onBatteriesChange={setBatteries}
            onPropellersChange={setPropellers}
            onConfigChange={setSystemConfig}
            onAnalysisResultsChange={setAnalysisResults}
            showStatus={showStatus}
          />
        )}
      </main>

      {/* Status Notification */}
      {statusMessage && (
        <StatusNotification
          message={statusMessage}
          type={statusType}
          onClose={() => setStatusMessage('')}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App; 